/**
 * Device Detection Utilities
 * Detect device type and determine if mobile UI should be used
 */

/**
 * Check if the device is a mobile device (phone or tablet)
 * @returns {boolean} True if mobile device
 */
export const isMobileDevice = () => {
  // Check user agent for mobile indicators
  const mobileRegex = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileUA = mobileRegex.test(navigator.userAgent);
  
  // Check for touch support
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Check screen size (mobile/tablet typically <= 1024px)
  const isSmallScreen = window.innerWidth <= 1024;
  
  return (isMobileUA || hasTouch) && isSmallScreen;
};

/**
 * Check if mobile UI should be used
 * @returns {boolean} True if mobile UI should be displayed
 */
export const shouldUseMobileUI = () => {
  // Don't use mobile UI in Electron app
  if (window.electronAPI) {
    return false;
  }
  
  // Also check window width for browser testing
  // This allows testing mobile view by resizing browser
  const isNarrowScreen = window.innerWidth <= 768;
  
  return isMobileDevice() || isNarrowScreen;
};

/**
 * Check if device is a tablet (iPad, Android tablet)
 * @returns {boolean} True if tablet
 */
export const isTablet = () => {
  const tabletRegex = /iPad|Android/i;
  const isTabletUA = tabletRegex.test(navigator.userAgent);
  const isTabletSize = window.innerWidth >= 768 && window.innerWidth <= 1024;
  
  return isTabletUA && isTabletSize;
};

/**
 * Check if device is a phone (smaller than tablet)
 * @returns {boolean} True if phone
 */
export const isPhone = () => {
  return isMobileDevice() && !isTablet();
};

/**
 * Get device type as string
 * @returns {'desktop' | 'tablet' | 'phone' | 'electron'}
 */
export const getDeviceType = () => {
  if (window.electronAPI) {
    return 'electron';
  }
  
  if (isTablet()) {
    return 'tablet';
  }
  
  if (isPhone()) {
    return 'phone';
  }
  
  return 'desktop';
};

/**
 * Check if device supports touch
 * @returns {boolean} True if touch is supported
 */
export const supportsTouchscreen = () => {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
};

/**
 * Get safe area insets for notched devices (iPhone X+)
 * @returns {Object} Insets in pixels
 */
export const getSafeAreaInsets = () => {
  // Try to get CSS environment variables
  const style = getComputedStyle(document.documentElement);
  
  return {
    top: parseInt(style.getPropertyValue('env(safe-area-inset-top)') || '0', 10),
    right: parseInt(style.getPropertyValue('env(safe-area-inset-right)') || '0', 10),
    bottom: parseInt(style.getPropertyValue('env(safe-area-inset-bottom)') || '0', 10),
    left: parseInt(style.getPropertyValue('env(safe-area-inset-left)') || '0', 10),
  };
};

